import { useManager } from 'dreamstate';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { rem, Flex, Header, Paragraph } from '@vimeo/bokeh';
import { Chats } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Callable, Optional } from '@vimeo/core/types/generic';
import { ECredentialsType } from '@vimeo/live-core/api/interaction';
import { interactionToolsConfig } from '@vimeo/live-core/configs/InteractionToolsConfig';
import {
  IInteractionSessionContext,
  InteractionSessionManager,
} from '@vimeo/live-core/store/interaction/session';
import { T_ERROR_BECAUSE_OF_PRIVACY_CANNOT_JOIN } from '@vimeo/live-core/translations';
import {
  EInteractionAuthOption,
  EInteractionRoomPrivacy,
} from '@vimeo/live-core/types/interaction_room';
import { stopEventPropagation } from '@vimeo/live-core/utils/dom/event';
import { Logger, useLogger } from '@vimeo/logging';
import { useBroadcastChannel, IChannelMessage } from '@vimeo/use-broadcast-channel';

import { createInteractionDomName } from '../../../../../libs/shared/utils/dom/identifying';
import { translations } from '../../../../../translations';
import { IconCircle } from '../../../shared/IconCircle';
import { InteractionPasswordLayout } from '../privacy';
import { PAGE_WRAPPER_STYLE } from '../styles';
import { InteractionNoAccessLayout } from './InteractionNoAccessLayout';
import { AdminLogin } from './options/AdminLogin';
import { AnonymousLogin } from './options/AnonymousLogin';
import { RegistrantLogin } from './options/RegistrantLogin';
import { VimeoLogin } from './options/VimeoLogin';
import { IInteractionLoginContext } from './shared/InteractionLoginContext';
import { InteractionLoginContextProvider } from './shared/InteractionLoginContextProvider';
import {
  LOGIN_CONTEXT_LABELS_STYLE,
  LOGIN_CONTAINER_WRAPPER_STYLE,
  LOGIN_SCREEN_WRAPPER_STYLE,
} from './styles';

interface IInteractionLoginLayoutProps extends IBaseDomElementProps {
  interactionSessionContext?: IInteractionSessionContext;
}

/**
 * Layout to perform login in interaction rooms.
 */
export function InteractionLoginLayout({
  id = createInteractionDomName('widget-login'),
  interactionSessionContext: {
    sessionId,
    roomInfo: { value: roomInfo },
    roomUser: { value: roomUser },
    roomPassword,
    roomHashedPassword,
    interactionSessionActions: { checkPasswordAuth, authorizeInteractionUser },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(InteractionSessionManager),
}: IInteractionLoginLayoutProps) {
  const log: Logger = useLogger('📡 BC in InteractionLoginLayout');

  const [accessBlockedReason, setAccessBlockedReason] = useState<Optional<string>>(null);
  const [isTermsLabelEnabled, setIsTermsLabelEnabled] = useState<boolean>(true);
  const [joinLabel, setJoinLabel] = useState<string>(translations.joinTheAudienceToParticipate);
  const [isJoinLabelIconEnabled, setJoinLabelIconEnabled] = useState<boolean>(true);

  const authOptions: Record<EInteractionAuthOption, boolean> =
    roomInfo?.settings.authOptions || ({} as Record<EInteractionAuthOption, boolean>);
  const isWithAdminAccess: boolean = Boolean(authOptions[EInteractionAuthOption.ADMIN] === true);

  const isRoomPasswordNeeded: boolean = Boolean(
    roomInfo?.settings.privacy === EInteractionRoomPrivacy.PASSWORD &&
      !roomPassword &&
      !roomHashedPassword,
  );
  const isRegistrantUuidNeeded: boolean = Boolean(
    authOptions[EInteractionAuthOption.REGISTRANT] === true && !roomUser?.id,
  );

  // Options that can be used in generic list.
  const availableGenericLoginOptionsCount: number = Object.entries(authOptions).filter(
    ([name, isEnabled]) => {
      return (
        isEnabled && name !== EInteractionAuthOption.ADMIN && name !== EInteractionAuthOption.GUEST
      );
    },
  ).length;

  const loginContext: IInteractionLoginContext = useMemo(() => {
    return {
      sessionId: sessionId.toString(),
      joinLabel,
      isTermsLabelEnabled,
      isJoinLabelIconEnabled,
      isAccessBlocked: Boolean(accessBlockedReason),
      accessBlockedReason: accessBlockedReason,
      setIsTermsLabelEnabled,
      setJoinLabelIconEnabled,
      setAccessBlocked: setAccessBlockedReason,
      setJoinLabels: (label: string) => {
        setJoinLabel(label);
        setJoinLabelIconEnabled(true);
      },
      resetLabels: () => {
        setJoinLabel(translations.joinTheAudienceToParticipate);
        setIsTermsLabelEnabled(true);
      },
    };
  }, [accessBlockedReason, isJoinLabelIconEnabled, isTermsLabelEnabled, joinLabel, sessionId]);

  const newDataUnsubscriberRef = useRef<Optional<Callable>>(null);

  const { isChannelActive, sendChannelMessage, subscribe, closeChannel } = useBroadcastChannel({
    name: interactionToolsConfig.BROADCAST_CHANNEL.NAME,
  });

  const onUnsubscribeFromNewData = useCallback(() => {
    if (newDataUnsubscriberRef.current) {
      log.info('Unsubscribe from new_data messages');

      newDataUnsubscriberRef.current();
    }
  }, [log]);

  const onSubscribeToNewData = useCallback(() => {
    if (!newDataUnsubscriberRef.current) {
      let messageReceived = false;

      newDataUnsubscriberRef.current = subscribe(function subscribeToNewData(
        message: IChannelMessage,
      ) {
        log.info('Subscribe to new_data messages');

        if (
          message.type !== interactionToolsConfig.BROADCAST_CHANNEL.MESSAGE_TYPE.NEW_DATA ||
          String(message?.payload?.sessionId) !== String(sessionId) ||
          messageReceived
        ) {
          return;
        }

        log.info('Receive new_data message:', message);

        if (
          message.payload.type === ECredentialsType.PASSWORD ||
          message.payload.type === ECredentialsType.HASHED_PASSWORD
        ) {
          messageReceived = true;
          checkPasswordAuth(String(message.payload.value), message.payload.type).finally(
            () => (messageReceived = false),
          );
        }

        if (message.payload.type === 'registrant_uuid') {
          messageReceived = true;
          authorizeInteractionUser({
            type: EInteractionAuthOption.REGISTRANT,
            roomRegistrantUuid: message.payload.value,
          }).finally(() => (messageReceived = false));
        }
      });
    }
  }, [authorizeInteractionUser, checkPasswordAuth, log, sessionId, subscribe]);

  useEffect(() => {
    if (!isChannelActive) {
      return;
    }

    if (isRoomPasswordNeeded || isRegistrantUuidNeeded) {
      onSubscribeToNewData();

      if (isRoomPasswordNeeded) {
        log.info('Post ask_data message for:', ECredentialsType.PASSWORD);

        sendChannelMessage({
          type: interactionToolsConfig.BROADCAST_CHANNEL.MESSAGE_TYPE.ASK_DATA,
          payload: { sessionId: String(sessionId), type: ECredentialsType.PASSWORD },
        });

        log.info('Post ask_data message for:', ECredentialsType.HASHED_PASSWORD);

        sendChannelMessage({
          type: interactionToolsConfig.BROADCAST_CHANNEL.MESSAGE_TYPE.ASK_DATA,
          payload: { sessionId: String(sessionId), type: ECredentialsType.HASHED_PASSWORD },
        });
      }

      if (isRegistrantUuidNeeded) {
        log.info('Post ask_data message for:', 'registrant_uuid');

        sendChannelMessage({
          type: interactionToolsConfig.BROADCAST_CHANNEL.MESSAGE_TYPE.ASK_DATA,
          payload: { sessionId: String(sessionId), type: 'registrant_uuid' },
        });
      }
    } else {
      onUnsubscribeFromNewData();
    }
  }, [
    sessionId,
    isChannelActive,
    isRegistrantUuidNeeded,
    isRoomPasswordNeeded,
    log,
    onSubscribeToNewData,
    onUnsubscribeFromNewData,
    sendChannelMessage,
  ]);

  useEffect(() => {
    return () => {
      onUnsubscribeFromNewData();
      log.info('Close channel');
      closeChannel();
    };
  }, [log, onUnsubscribeFromNewData, closeChannel]);

  // Access blocked when event is ended / rate limit for login fired etc.
  if (accessBlockedReason) {
    return (
      <InteractionLoginContextProvider value={loginContext}>
        <InteractionNoAccessLayout
          id={createInteractionDomName(id, 'access-blocked')}
          isWithIcon={true}
          reason={accessBlockedReason}
        />
      </InteractionLoginContextProvider>
    );
    // Password needed to access further steps.
  } else if (isRoomPasswordNeeded) {
    return (
      <InteractionLoginContextProvider value={loginContext}>
        <InteractionPasswordLayout id={createInteractionDomName(id, 'password-layout')} />
      </InteractionLoginContextProvider>
    );
    // No auth options and no admin access to bypass it.
  } else if (!availableGenericLoginOptionsCount && !isWithAdminAccess) {
    return (
      <InteractionLoginContextProvider value={loginContext}>
        <InteractionNoAccessLayout
          id={createInteractionDomName(id, 'no-access')}
          reason={T_ERROR_BECAUSE_OF_PRIVACY_CANNOT_JOIN}
        />
      </InteractionLoginContextProvider>
    );
  }

  return (
    <InteractionLoginContextProvider value={loginContext}>
      <Flex id={id} sx={PAGE_WRAPPER_STYLE}>
        <Flex
          id={createInteractionDomName(id, 'content')}
          sx={LOGIN_SCREEN_WRAPPER_STYLE}
          onKeyUp={stopEventPropagation}
          onKeyDown={stopEventPropagation}
          onKeyPress={stopEventPropagation}
        >
          <Flex id={createInteractionDomName(id, 'form')} sx={LOGIN_CONTAINER_WRAPPER_STYLE}>
            <Flex
              id={createInteractionDomName(id, 'context-labels')}
              sx={LOGIN_CONTEXT_LABELS_STYLE}
            >
              {isJoinLabelIconEnabled ? (
                <IconCircle id={createInteractionDomName(id, 'context-icon')}>
                  <Chats boxSize={'md'} />
                </IconCircle>
              ) : null}

              <Header
                id={createInteractionDomName(id, 'context-header')}
                size={'md'}
                textAlign={'center'}
              >
                {joinLabel}
              </Header>
            </Flex>

            {Boolean(authOptions[EInteractionAuthOption.ANONYMOUS]) ? (
              <AnonymousLogin id={createInteractionDomName(id, 'anonymous-login')} />
            ) : null}

            {Boolean(authOptions[EInteractionAuthOption.REGISTRANT]) ? (
              <RegistrantLogin id={createInteractionDomName(id, 'registrant-login')} />
            ) : null}

            {Boolean(authOptions[EInteractionAuthOption.VIMEO]) ? (
              <VimeoLogin id={createInteractionDomName(id, 'vimeo-login')} />
            ) : null}

            {isTermsLabelEnabled && availableGenericLoginOptionsCount > 0 ? (
              <Paragraph
                size={'md'}
                textAlign={'center'}
                color={'text-secondary'}
                margin={`${rem(8)} 0`}
              >
                {translations.contentYouSubmitThroughChatIsSubjectToVimeosTermsOfServiceAndPrivacyPolicy()}
              </Paragraph>
            ) : null}
          </Flex>

          {Boolean(authOptions[EInteractionAuthOption.ADMIN]) ? (
            <AdminLogin id={createInteractionDomName(id, 'admin-login')} />
          ) : null}
        </Flex>
      </Flex>
    </InteractionLoginContextProvider>
  );
}
